import React, { useState } from 'react';
import {
  Input,
  Select,
  DatePicker
} from 'syngenta-digital-cropwise-react-ui-kit';
import useLandingPageData from 'hooks/useLandingPageData';
import ChevronDown from 'assets/chevron_down.svg';
import PropTypes from 'prop-types';
import AddressFields from '../AddressFields';

const AddPerson = ({ values, onChanges, errors, onBlur, touched }) => {
  const [companiesSelectOpen, setCompaniesSelectOpen] = useState(false);
  const { loaded, items: companies } = useLandingPageData('worker/companies', [
    'company.name'
  ]);

  const handleDateChange = date => {
    // Keep the date object in Formik state, will convert to epoch in parent when submitting
    onChanges({
      target: {
        name: 'applicatorLicenseExpirationDate',
        value: date
      }
    });
  };

  return (
    <>
      <div className="flex flex-col mb-5 w-1/2 pr-4 relative">
        <label
          htmlFor="owner-name"
          className="text-neutral-60 tracking-tighter text-sm mb-0.5"
        >
          Name<span className="text-remove">*</span>
        </label>
        <Input
          required
          name="name"
          id="owner-name"
          type="text"
          size="middle"
          value={values.name}
          onChange={onChanges}
          onBlur={onBlur}
          status={touched?.name && errors?.name ? 'error' : undefined}
        />
        {touched?.name && errors?.name && (
          <span
            className="text-remove text-xs absolute"
            style={{ top: '100%' }}
          >
            {errors.name}
          </span>
        )}
      </div>
      <h3 className="text-base font-semibold block mb-4 mt-10px">Basic Info</h3>

      <div className="flex mb-5">
        <div className="flex flex-col w-full mr-8">
          <label
            htmlFor="email"
            className="text-neutral-60 tracking-tighter text-sm mb-0.5"
          >
            Email Address
          </label>
          <Input
            type="text"
            size="middle"
            name="email"
            id="email"
            value={values.email}
            status={touched?.email && errors?.email ? 'error' : undefined}
            onChange={onChanges}
          />
          {touched?.email && errors?.email && (
            <span className="text-remove text-xs mt-1">{errors.email}</span>
          )}
        </div>

        <div className="flex flex-col w-full">
          <label
            htmlFor="phone"
            className="text-neutral-60 tracking-tighter text-sm mb-0.5"
          >
            Phone Number
          </label>
          <Input
            type="text"
            size="middle"
            name="phone"
            id="phone"
            value={values.phone}
            onChange={onChanges}
          />
        </div>
      </div>
      <div className="flex flex-col mb-5 w-1/2 pr-4">
        <label
          htmlFor="company"
          className="text-neutral-60 tracking-tighter text-sm mb-0.5"
        >
          Company
        </label>
        <Select
          value={values.company}
          onDropdownVisibleChange={selectOpen =>
            setCompaniesSelectOpen(selectOpen)
          }
          getPopupContainer={triggerNode =>
            triggerNode.parentElement || document.body
          }
          suffixIcon={
            <img
              src={ChevronDown}
              style={{
                transform: companiesSelectOpen ? 'rotate(180deg)' : null
              }}
              height={18}
              width={18}
              alt="Select dropdown icon"
            />
          }
          loading={!loaded}
          id="company"
          onChange={onChanges}
        >
          {(companies || [])
            .slice()
            .sort((a, b) =>
              a.name
                .trim()
                .toLowerCase()
                .localeCompare(b.name.trim().toLowerCase(), undefined, {
                  numeric: true
                })
            )
            .map(company => (
              <Select.Option key={company.id} value={company.id}>
                {company.name}
              </Select.Option>
            ))}
        </Select>
      </div>
      <h3 className="text-base font-semibold block mb-4">Address</h3>
      <div className="relative grid gap-x-4 -mr-4 grid-cols-4">
        <AddressFields />
      </div>
      <h3 className="text-base font-semibold block mb-4 mt-2">License Info</h3>
      <div className="flex mb-5">
        <div className="flex flex-col w-full mr-8">
          <label
            htmlFor="applicatorLicense"
            className="text-neutral-60 tracking-tighter text-sm block mb-0.5"
          >
            Applicator License
          </label>
          <Input
            type="text"
            size="middle"
            className="m-0"
            name="applicatorLicenseNumber"
            id="applicatorLicenseNumber"
            value={values.applicatorLicenseNumber}
            onChange={onChanges}
          />
        </div>

        <div className="flex flex-col w-full relative">
          <label
            htmlFor="applicatorLicenseExpirationDate"
            className="text-neutral-60 tracking-tighter text-sm block mb-0.5"
          >
            Valid Through
          </label>
          <DatePicker
            name="applicatorLicenseExpirationDate"
            id="applicatorLicenseExpirationDate"
            format="MM-DD-YYYY"
            placeholder=" "
            value={values.applicatorLicenseExpirationDate}
            onChange={handleDateChange}
            style={{ margin: '2px 0' }}
            getPopupContainer={triggerNode =>
              triggerNode.parentElement || document.body
            }
          />
        </div>
      </div>
      <div className="my-8 relative">
        <label
          htmlFor="notes"
          className="text-neutral-60 tracking-tighter text-sm block mb-0.5"
        >
          Notes
        </label>
        <Input.TextArea
          value={values.notes}
          id="notes"
          name="notes"
          className="h-20"
          onChange={onChanges}
        />
      </div>
    </>
  );
};

AddPerson.defaultProps = {
  errors: null,
  touched: null
};

AddPerson.propTypes = {
  values: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    birthday: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    company: PropTypes.string,
    companyId: PropTypes.string,
    addressLineOne: PropTypes.string,
    addressLineTwo: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    applicatorLicenseNumber: PropTypes.string,
    applicatorLicenseExpirationDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
    notes: PropTypes.string
  }).isRequired,
  onChanges: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  touched: PropTypes.objectOf(PropTypes.bool)
};

export default AddPerson;
